import React from "react";
import PropTypes from "prop-types";
import { defaultProps } from "./implementation/defaultProps";
import Animation from "./implementation/Animation";
import { computeCurrentSlide } from "./implementation/utils";

export default class Carousel extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            animating: false,
            currentSlide: this.props.currentSlide || defaultProps.currentSlide,
            contentStyle: this.getWrapperStyle()
        };
        this.carouselWrapper = React.createRef();
        this.Animation = new Animation(this.props);
        this.bindEventHandlers();
    }

    componentDidMount() {
        if (!this.props.children) {
            return;
        }

        const style = this.Animation.computeTransition(
            this.state.currentSlide,
            this.state.contentStyle,
            this.carouselWrapper
        );
        this.timer = setTimeout(
            this.setState({ contentStyle: style }, () => {
                clearTimeout(this.timer);
                this.timer = undefined;
            }),
            3
        );
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.currentSlide !== this.state.currentSlide) {
            this.moveToSlide(nextProps.currentSlide);
        }
    }

    bindEventHandlers() {
        // Make sure event handlers have the correct context
        this.decrement = this.decrement.bind(this);
        this.increment = this.increment.bind(this);
    }

    moveToSlide(slideIndex) {
        // Functionality of animateSlide and postSlide is merged into this function
        // console.log("slideHandler", slideIndex);
        const slidesCount = this.props.children.length;

        if (this.state.animating) {
            return;
        }

        // Horizontal and Vertical slides animation
        const targetSlide = slideIndex;
        const currentSlide = computeCurrentSlide(slideIndex, slidesCount);

        // Slide Transition happens here.
        // Animated transition happens to targetSlide and
        // non-animated transition happens to currentSlide.
        this.setState(
            {
                animating: true,
                currentSlide: currentSlide,
                contentStyle: this.Animation.computeAnimateTransition(
                    targetSlide,
                    this.state.contentStyle,
                    this.carouselWrapper
                )
            },
            () => {
                this.animationEndTimer = setTimeout(() => {
                    this.setState({
                        animating: false,
                        contentStyle: this.Animation.computeTransition(
                            currentSlide,
                            this.state.contentStyle,
                            this.carouselWrapper
                        )
                    });

                    if (this.props.onChange) {
                        this.props.onChange(this.state);
                    }
                    clearTimeout(this.animationEndTimer);
                    this.animationEndTimer = undefined;
                }, defaultProps.animationTimeInterval);
            }
        );
    }

    decrement(event) {
        if (event) {
            event.stopPropagation();
        }
        const slidesToScroll = 1;
        this.moveToSlide(this.state.currentSlide - slidesToScroll);
    }

    increment(event) {
        if (event) {
            event.stopPropagation();
        }
        const slidesToScroll = 1;
        this.moveToSlide(this.state.currentSlide + slidesToScroll);
    }

    buildContainerClass() {
        let className = "tq-carousel un-carousel-inner-gallery ";
        className += this.props.extraClasses || "";

        return className;
    }

    renderSlides() {
        const slidesContent = [],
            preCloneSlides = [],
            postCloneSlides = [];
        const slidesCount = React.Children.count(this.props.children);

        React.Children.forEach(this.props.children, (currentSlide, index) => {
            if (!React.isValidElement(currentSlide)) return;
            const className = "tq-slide";
            let key;
            const visibleSlides = 1;

            slidesContent.push(
                <div key={"original" + index} data-index={index} className={className}>
                    {currentSlide}
                </div>
            );

            if (index >= slidesCount - visibleSlides) {
                key = -(slidesCount - index);
                preCloneSlides.push(
                    <div key={"precloned" + key} data-index={key} className="tq-slide">
                        {currentSlide}
                    </div>
                );
            }

            if (index < visibleSlides + 1) {
                key = slidesCount + index;
                postCloneSlides.push(
                    <div key={"postcloned" + key} data-index={key} className="tq-slide">
                        {currentSlide}
                    </div>
                );
            }
        });

        return preCloneSlides.concat(slidesContent, postCloneSlides);
    }

    buildSlideControls() {
        // carousel slide arrows
        const prevArrowClass = "tq-icon-arrow-left-big";
        const nextArrowClass = "tq-icon-arrow-right-big";

        const prevArrow = (
            <div className="tq-arrow tq-prev" onClick={this.decrement}>
                <i className={prevArrowClass} />
            </div>
        );
        const nextArrow = (
            <div className="tq-arrow tq-next" onClick={this.increment}>
                <i className={nextArrowClass} />
            </div>
        );

        return { prevArrow, nextArrow };
    }

    getWrapperStyle() {
        // Each Carousel child can be placed in a single slide.
        // For an animated transition, it is necessary to calculate the width/height of the container.
        // And slide width/height percent depend of direction (horizontal or vertical).
        const wrapperStyle = {};

        // Total count children + cloning
        const slidesCount = this.props.children.length + 3;
        // Consider dimension by 100% * slidesCount.
        wrapperStyle.width = 100 * slidesCount + "%";

        return wrapperStyle;
    }

    render() {
        const containerClass = this.buildContainerClass();
        const slidesContent = this.renderSlides();
        const { prevArrow, nextArrow } = this.buildSlideControls();

        return (
            <div className={containerClass} ref={this.carouselWrapper}>
                <div className="tq-carousel-content" style={this.state.contentStyle}>
                    {slidesContent}
                </div>
                {prevArrow}
                {nextArrow}
            </div>
        );
    }
}

Carousel.displayName = "Carousel";

Carousel.propTypes = {
    extraClasses: PropTypes.string,
    children: PropTypes.node,
    currentSlide: PropTypes.number,
    onChange: PropTypes.func
};
